import TradePriceCache from '../../../../lib/store-util/TradePriceCache';
import { formatCurrencyBySymbol } from '../../../../lib/util/DataHelpers';
import { calculatePriceMovement, roundToCurrencyFormat } from '../../../../lib/util/lastPriceChangeHelpers';
import { getSymbolFirstPart } from '../../../../lib/util/MarketDataHelpers';

import { SymbolTableGrid } from '../../../../components/SymbolTable/SymbolTableGrid.enum';
import SymbolTableRow from '../../../../components/SymbolTable/SymbolTableRow/SymbolTableRow';
import useBrowserWidth from '../../../hooks/useBrowserWidth';
import useIsMobile from '../../../hooks/useIsMobile';
import { PopularGroupTableItemProps } from '../../PopularGroups.types';

/** Below this the symbol and its name share a single line. */
const ONE_ROW_INFO_MAX_WIDTH = 1280;

const PopularGroupTableItem = ({ row, isWeek52Item, enableToggle, onSymbolClick }: PopularGroupTableItemProps) => {
  const { item, chartSymbol, currencySymbol, isEuropeanSymbol, countryDesignation, companyName } = row;
  const { isMobile } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();

  const symbolForPriceCache = getSymbolFirstPart(chartSymbol);
  const { direction, change, changePercentWithSign, currentPrice } = TradePriceCache.use(
    symbolForPriceCache,
    'PopularGroupTableItem',
  );

  // European prices keep their own precision; everything else is rounded for the narrow mobile cell.
  const mobileLastPrice = isEuropeanSymbol ? (currentPrice ?? 0) : roundToCurrencyFormat(currentPrice || 0);
  const lastPriceValue = isMobile ? mobileLastPrice : currentPrice;
  const currencyOptions = { countryDesignation, isEuropeanSymbol };
  const priceMovement = calculatePriceMovement(currentPrice, chartSymbol);

  // Ace's SymbolTableRow has no `enableToggle`, so it cannot be passed statically.
  // A JSX spread skips excess-property checking and compiles against both apps.
  const toggleProps = enableToggle ? { enableToggle: true } : {};

  return (
    <SymbolTableRow
      id={item.symbol}
      name={companyName}
      onRowClick={() => onSymbolClick(item.symbol)}
      isWeek52Item={isWeek52Item}
      oneRowInfoContent={isWeek52Item || browserWidthSize < ONE_ROW_INFO_MAX_WIDTH}
      gridTemplateColumns={SymbolTableGrid.FIVE_ROW_ITEMS_GRID}
      isWithNews={!isWeek52Item}
      {...toggleProps}
      model={{
        stock: item,
        chartSymbol,
        priceMovement,
        direction,
        changePercent: changePercentWithSign,
        lastPrice: formatCurrencyBySymbol(lastPriceValue, currencySymbol, currencyOptions),
        changePrice: isWeek52Item
          ? changePercentWithSign
          : formatCurrencyBySymbol(change, currencySymbol, currencyOptions),
      }}
    />
  );
};

export default PopularGroupTableItem;
